import type { AcuteDisposition, AcuteEpisode } from '@liza/fhir-utils';
import { format, isSameDay, isSameMonth, isSameYear, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { DATE_FORMATS } from '../../utils/date';

/**
 * A compact range for an episode: one date when it starts and ends on the same day, "Jun 26–30, 2026"
 * within a month, "Aug 31 – Sep 2, 2026" across months, and two full dates across years. Date-only
 * starts (Care Everywhere copies) are parsed as local midnight by `parseISO`, so the day is not shifted.
 */
export const formatEpisodeDateRange = (start: string, end: string | undefined): string => {
  const from = parseISO(start);
  if (!end) return format(from, DATE_FORMATS.DATE);
  const to = parseISO(end);
  if (isSameDay(from, to)) return format(from, DATE_FORMATS.DATE);
  if (isSameMonth(from, to)) return `${format(from, 'MMM d')}–${format(to, 'd, yyyy')}`;
  if (isSameYear(from, to)) return `${format(from, DATE_FORMATS.SHORT_DATE)} – ${format(to, DATE_FORMATS.DATE)}`;
  return `${format(from, DATE_FORMATS.DATE)} – ${format(to, DATE_FORMATS.DATE)}`;
};

/** Whole minutes as "2 h 24 min", "3 h" or "45 min". */
export const formatDurationMinutes = (minutes: number, t: TFunction<'records'>): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t('durationMinutes', { count: rest });
  if (rest === 0) return t('durationHours', { count: hours });
  return t('durationHoursMinutes', { hours, minutes: rest });
};

/** Length of stay or duration for the episode header, or the discharge-pending copy when there is no end. */
export const formatEpisodeLength = (episode: AcuteEpisode, t: TFunction<'records'>): string => {
  if (!episode.dischargeRecorded) return t('dischargeNotYetRecorded');
  if (episode.kind === 'admission' && episode.lengthOfStayDays !== undefined) {
    return episode.lengthOfStayDays === 0 ? t('sameDayStay') : t('lengthOfStay', { count: episode.lengthOfStayDays });
  }
  return episode.durationMinutes !== undefined ? formatDurationMinutes(episode.durationMinutes, t) : '';
};

export const formatDisposition = (disposition: AcuteDisposition, t: TFunction<'records'>): string => {
  switch (disposition) {
    case 'home':
      return t('dispositionHome');
    case 'facility':
      return t('dispositionFacility');
    case 'leftAgainstAdvice':
      return t('dispositionLeftAgainstAdvice');
    case 'leftBeforeSeen':
      return t('dispositionLeftBeforeSeen');
    case 'admitted':
      return t('dispositionAdmitted');
    case 'other':
      return t('dispositionOther');
    default:
      return t('dispositionNotRecorded');
  }
};

export const acuteEpisodeKindLabel = (kind: AcuteEpisode['kind'], t: TFunction<'records'>): string =>
  kind === 'admission' ? t('hospitalStay') : t('erVisit');

/** Marker colours shared by the list rows, the summary and the care-plan timeline glyphs. */
export const ACUTE_EPISODE_DOT_CLASS: Record<AcuteEpisode['kind'], string> = {
  edVisit: 'bg-orange-500',
  admission: 'bg-violet-500',
};

/** The hollow variant: discharge not recorded, or the patient left against advice. */
export const ACUTE_EPISODE_HOLLOW_DOT_CLASS: Record<AcuteEpisode['kind'], string> = {
  edVisit: 'border-2 border-orange-500 bg-transparent',
  admission: 'border-2 border-violet-500 bg-transparent',
};

/** Episodes bucketed by the calendar year they started, newest year first, preserving episode order. */
export const groupEpisodesByYear = (episodes: AcuteEpisode[]): { year: string; episodes: AcuteEpisode[] }[] => {
  const byYear = new Map<string, AcuteEpisode[]>();
  for (const episode of episodes) {
    const year = episode.start.slice(0, 4);
    const bucket = byYear.get(year);
    if (bucket) bucket.push(episode);
    else byYear.set(year, [episode]);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([year, grouped]) => ({ year, episodes: grouped }));
};
